import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ENGINES = {
  ai_native: {
    code: 'ENGINE 01',
    name: '100% AI-Native Production',
    tagline: 'From script & prompt to 4K master — zero physical film crew required.',
    speed: '3–5x Faster Turnaround',
    ownership: '100% Client-Owned Commercial IP',
    description:
      'We generate entire worlds, consistent characters, haute couture commercials, serialized 9:16 micro-dramas, and original films purely through our proprietary multi-model AI pipeline.',
    deliverables: [
      {
        title: 'Brand Ads, AI UGC & D2C Product Spots',
        detail: 'Alcobrew BHRUM, Vendoz Bridal Sneakers, Zoom Shoes, MHPU, Fine Jewellery UGC, Fintech Creator Spots, Sony, Haute Couture & Fevi Stick.',
      },
      {
        title: '9:16 Serialized Micro-Dramas',
        detail: 'High-retention vertical series produced for DramaBox (Anna, Iron Horizon) and Dashverse (Chronicles Ep 09 & 11).',
      },
      {
        title: 'Originals & Official Music Videos',
        detail: 'Flagship action thriller Ashes of Fire ★, mythological epic Jatayu, Flamingos, Mental, Aasmaan & Run.',
      },
    ],
    stack: ['Seedance 2.0', 'Multi-Keyframe Character Lock', 'ComfyUI Custom Nodes', 'DaVinci Resolve Finishing'],
  },
  hybrid_vfx: {
    code: 'ENGINE 02',
    name: 'Hybrid Live-Action + AI VFX',
    tagline: 'Real actors & cinema camera plates transformed frame-by-frame with AI VFX.',
    speed: '60% Lower VFX Cost vs. Legacy CGI',
    ownership: 'Theatrical 2K/4K ProRes 4444 Masters',
    description:
      'We take real green-screen or on-location footage shot on ARRI/RED and run it through our Hybrid AI VFX pipeline — delivering photoreal environment extension, neural relighting, and crowd synthesis.',
    deliverables: [
      {
        title: 'Telugu Feature Film VFX',
        detail: 'Theatrical VFX sequences delivered for #CULT (Vishwak Sen), HONEY (Naveen Chandra), MANODU G.O.A.T (Sudigali Sudheer) & Rudra.',
      },
      {
        title: 'Hybrid Broadcast Commercials',
        detail: 'Live-action + AI VFX campaigns for BKIC Insurance (Ramadan Campaign), Seef Properties, XPENG G9 & Bookmark C&Q.',
      },
      {
        title: 'Neural Relighting & Set Extension',
        detail: '40,000-seat stadium extensions, exo-suit transformations, and physical PBR relighting via Beeble SwitchX.',
      },
    ],
    stack: ['ARRI / RED Camera Plates', 'Beeble SwitchX Relighting', 'AI Set & Crowd Synthesis', 'Nuke / ACES Color Pipeline'],
  },
};

export default function ServicesSection() {
  const [selectedEngine, setSelectedEngine] = useState('ai_native');
  const active = ENGINES[selectedEngine];

  return (
    <section
      id="capabilities"
      className="bg-black py-20 sm:py-28 border-t border-white/[0.08] w-full"
    >
      <div className="w-full px-5 sm:px-10 lg:px-16 xl:px-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-10 border-b border-white/[0.08]">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#E50914] mb-2">
              ● Interactive Dual-Engine Switchboard
            </p>
            <h2 className="text-white font-normal tracking-[-0.03em] leading-[1.05] text-[clamp(1.85rem,3.4vw,3.25rem)]">
              Two ways to build the impossible.
            </h2>
          </div>

          {/* Interactive Engine Switcher */}
          <div className="inline-flex p-1 rounded-full bg-white/[0.06] border border-white/10 self-start lg:self-end">
            <button
              type="button"
              onClick={() => setSelectedEngine('ai_native')}
              className={`px-5 py-2.5 rounded-full text-[13px] transition-all cursor-pointer ${
                selectedEngine === 'ai_native'
                  ? 'bg-[#E50914] text-white font-medium shadow-[0_0_20px_rgba(229,9,20,0.35)]'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Engine 01 · 100% AI-Native
            </button>
            <button
              type="button"
              onClick={() => setSelectedEngine('hybrid_vfx')}
              className={`px-5 py-2.5 rounded-full text-[13px] transition-all cursor-pointer ${
                selectedEngine === 'hybrid_vfx'
                  ? 'bg-[#E50914] text-white font-medium shadow-[0_0_20px_rgba(229,9,20,0.35)]'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Engine 02 · Hybrid Live-Action + VFX
            </button>
          </div>
        </div>

        {/* Active Engine Interactive Breakdown */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedEngine}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.28 }}
            className="pt-10 grid grid-cols-1 lg:grid-cols-[1.1fr_1.4fr] gap-10 lg:gap-16"
          >
            <div className="flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 text-[11px] font-mono text-white/60 mb-3">
                  <span>{active.code}</span>
                  <span>·</span>
                  <span>{active.speed}</span>
                </div>
                <h3 className="text-white font-normal tracking-[-0.03em] text-3xl sm:text-4xl leading-[1.06]">
                  {active.name}
                </h3>
                <p className="text-white/85 text-base sm:text-lg mt-3 font-normal">
                  {active.tagline}
                </p>
                <p className="text-[#767d88] text-sm sm:text-[15px] leading-relaxed mt-4">
                  {active.description}
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-[11px] font-mono uppercase tracking-[0.12em] text-[#767d88] mb-3">
                  Core Tooling &amp; Output Specs
                </p>
                <div className="flex flex-wrap gap-2">
                  {active.stack.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-white/80 text-[12px]"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {active.deliverables.map((d, i) => (
                <div
                  key={d.title}
                  className="p-6 rounded-xl bg-[#09090b] border border-white/[0.08] hover:border-white/25 transition-colors"
                >
                  <div className="flex items-center justify-between text-[11px] font-mono text-[#767d88] mb-2">
                    <span className="text-[#E50914] font-semibold">0{i + 1}</span>
                    <span>{active.ownership}</span>
                  </div>
                  <h4 className="text-[18px] font-medium tracking-[-0.02em] text-white">
                    {d.title}
                  </h4>
                  <p className="text-[14px] text-[#767d88] mt-1.5 leading-relaxed">
                    {d.detail}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
